import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  SkipSelf,
  ViewChild,
} from '@angular/core';
import { IconModule } from 'studio-components/icon/icon.module';
import { PopoverService } from '../services/popover.service';
import tippyJs, { Instance } from 'tippy.js';

@Component({
  selector: 'studio-popover',
  standalone: true,
  imports: [CommonModule, IconModule],
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
})
export class PopoverComponent implements AfterViewInit, OnDestroy {
  @ViewChild('contentEl') content: ElementRef<Element>;

  @Input() id: string;
  @Input() heading: string;
  @Input() body: string;
  @Input() trigger: 'click' | 'mouseenter focus' | 'manual' = 'click';
  @Input() theme: 'default' | 'semi-transparent' = 'default';
  @Input() showOnCreate: boolean;
  @Input() hideOnClick: boolean;
  @Input() showCloseButton: boolean;
  @Input() maxWidth: 'auto' | number = 'auto';
  @Input() xOffset: number;
  @Input() delay: number;
  @Input() duration: number;

  @Output() shown = new EventEmitter<void>();
  @Output() hidden = new EventEmitter<void>();

  private instance: Instance;
  private borderSize = 16;

  protected get isTooltip() {
    return this.trigger === 'mouseenter focus';
  }

  protected get displayCloseButton() {
    return this.showCloseButton ?? !this.isTooltip;
  }

  constructor(
    @SkipSelf() private triggerEl: ElementRef<Element>,
    private popoverService: PopoverService,
  ) {}

  ngAfterViewInit() {
    this.instance = tippyJs(this.triggerEl.nativeElement, {
      content: this.content.nativeElement,
      allowHTML: true,
      interactive: true,
      trigger: this.trigger,
      showOnCreate: this.showOnCreate,
      hideOnClick: this.hideOnClick,
      maxWidth: this.maxWidth,
      offset: [this.xOffset ?? 0, this.borderSize],
      arrow: false,
      placement: 'top-start',
      delay: this.delay ?? this.isTooltip ? 400 : 0,
      duration: this.duration,
      // nest the dynamically generated popover elements inside the trigger element
      // so they can be targetted using ng-deep within :host to avoid style leakage
      appendTo: this.triggerEl.nativeElement,
      // using onShow as found onShown unreliable, possibly it isn't working with ng-content
      onShow: () => this.shown.emit(),
      onHidden: () => this.hidden.emit(),
    });

    if (this.id) {
      this.popoverService.addInstance(this.id, this.instance);
    }
  }

  ngOnDestroy() {
    if (this.id) {
      this.popoverService.removeInstance(this.id);
    }

    this.instance.destroy();
  }

  protected close() {
    this.instance.hide();
  }
}
